import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { BASE_URL } from "../configs/WebServiceUrl";
import { useNavigate } from "react-router-dom";

const MobileForgotPassword = () => {
  const [mobile, setMobile] = useState("");
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const navigate = useNavigate();

  const handleOtpInput = (value, index) => {
    if (!/^\d?$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    // auto move to next input
    if (value && index < 5) {
      document.getElementById(`otp-${index + 1}`).focus();
    }
  };

  const sendMobileOtp = async (e) => {
    e.preventDefault();

    if (!/^\d{10}$/.test(mobile)) {
      alert("Enter valid 10-digit mobile number");
      return;
    }

    setLoading(true);

    try {
      const payload = {
        otpType: "mobile_fg",
        otpSentTo: mobile,
      };

      const res = await axios.post(
        `${BASE_URL}/api/adminauth/sendOTPMobileForgot`,
        payload
      );

      if (res.data?.success) {
        alert(res.data.message || "OTP Sent Successfully!");
        setStep(2);
      } else {
        alert(res.data?.message || "Failed to send OTP");
      }
    } catch (error) {
      console.log("Error:", error);

      if (error.response) {
        const code = error.response.status;
        const msg = error.response.data?.message;

        if (code === 400) {
          console.log(msg || "Bad Request");
        } else if (code === 404) {
          alert(msg || "Mobile number not found");
        } else if (code === 500) {
          console.log("Internal Server Error");
        } else {
          console.log(msg || "Something went wrong");
        }
      } else {
        console.log("Network error");
      }
    } finally {
      setLoading(false);
    }
  };

  // STEP 2 → Verify OTP
  const verifyMobileOtp = async (e) => {
    e.preventDefault();

    const finalOtp = otp.join("");

    if (finalOtp.length !== 6) {
      alert("Please enter valid 6-digit OTP");
      return;
    }

    setLoading(true);

    try {
      const payload = {
        mobile: mobile,
        otp: finalOtp,
      };

      const res = await axios.post(
        `${BASE_URL}/api/adminauth/verifyMobilefgOtp`,
        payload
      );

      if (res.status === 200 && res.data?.success) {
        alert(res.data.message || "OTP Verified!");
        setStep(3);
        return;
      }
    } catch (err) {
      if (err.response && err.response.status === 400) {
        alert(err.response.data?.message || "Invalid OTP");
      } else {
        alert("Server error while verifying OTP");
      }
    } finally {
      setLoading(false);
    }
  };

  const resetMobilePassword = async (e) => {
    e.preventDefault();

    if (newPassword !== confirmPassword) {
      alert("Passwords do not match");
      return;
    }

    setLoading(true);

    try {
      const payload = {
        mobile: mobile,
        password: newPassword,
      };

      const res = await axios.post(
        `${BASE_URL}/general/update-password`,
        payload
      );

      console.log("Reset Password Response:", res);

      if (res.status === 200 && res.data.success) {
        alert(res.data.message || "Password Reset Successful!");
        navigate("/login");
      } else {
        alert(res.data.message || "Failed to reset password");
      }
    } catch (err) {
      console.error("Reset Password Error:", err);
      if (err.response) {
        alert(err.response.data?.message || "Error resetting password");
      } else {
        alert("Network Error");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container d-flex justify-content-center align-items-center ">
      <motion.div
        className="card shadow p-4"
        style={{ width: "100%", maxWidth: "400px", borderRadius: "16px" }}
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
      >
        <AnimatePresence mode="wait">
          {/* STEP 1 - MOBILE INPUT */}
          {step === 1 && (
            <motion.form
              key="mobile"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              onSubmit={sendMobileOtp}
            >
              <label className="form-label fw-semibold">
                Enter Mobile Number
              </label>
              <input
                type="text"
                className="form-control"
                maxLength={10}
                value={mobile}
                onChange={(e) => setMobile(e.target.value.replace(/\D/, ""))}
                placeholder="10 digit mobile number"
                required
              />

              <button className="btn btn-primary w-100 mt-3" disabled={loading}>
                {loading ? "Sending..." : "Send OTP"}
              </button>
            </motion.form>
          )}

          {/* STEP 2 - OTP INPUT */}
          {step === 2 && (
            <motion.form
              key="otp"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              onSubmit={verifyMobileOtp}
            >
              <label className="form-label fw-semibold">
                Enter 6 Digit OTP
              </label>

              <div className="d-flex gap-2 justify-content-center mb-3">
                {otp.map((digit, index) => (
                  <input
                    key={index}
                    id={`otp-${index}`}
                    type="text"
                    maxLength={1}
                    value={digit}
                    className="form-control text-center"
                    style={{ width: "50px", fontSize: "20px" }}
                    onChange={(e) => handleOtpInput(e.target.value, index)}
                  />
                ))}
              </div>

              <button className="btn btn-success w-100" disabled={loading}>
                {loading ? "Verifying..." : "Verify OTP"}
              </button>
            </motion.form>
          )}

          {/* STEP 3 - RESET PASSWORD */}
          {/* STEP 3 - RESET PASSWORD */}
          {step === 3 && (
            <motion.form
              key="reset"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              onSubmit={resetMobilePassword}
            >
              {/* New Password */}
              <label className="form-label fw-semibold">New Password</label>
              <div style={{ position: "relative" }}>
                <input
                  type={showPassword ? "text" : "password"}
                  className="form-control mb-3"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                />

                <i
                  className={`fa ${showPassword ? "fa-eye-slash" : "fa-eye"}`}
                  style={{
                    position: "absolute",
                    right: "12px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    cursor: "pointer",
                    color: "#6c757d",
                  }}
                  onClick={() => setShowPassword(!showPassword)}
                ></i>
              </div>

              {/* Confirm Password */}
              <label className="form-label fw-semibold">Confirm Password</label>
              <div style={{ position: "relative" }}>
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  className="form-control mb-3"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                />

                <i
                  className={`fa ${
                    showConfirmPassword ? "fa-eye-slash" : "fa-eye"
                  }`}
                  style={{
                    position: "absolute",
                    right: "12px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    cursor: "pointer",
                    color: "#6c757d",
                  }}
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                ></i>
              </div>

              <button className="btn btn-success w-100" disabled={loading}>
                {loading ? "Updating..." : "Set Your Password"}
              </button>
            </motion.form>
          )}
        </AnimatePresence>

        <div className="text-center mt-3">
          <small
            style={{ cursor: "pointer", color: "#007bff" }}
            onClick={() => navigate("/login")}
          >
            ← Back to Login
          </small>
        </div>
      </motion.div>
    </div>
  );
};

export default MobileForgotPassword;
